import React, { useState } from 'react'
import { Suggestion } from '../../types/mockup'
import './NotificationPreview.css'

interface NotificationPreviewProps {
  suggestion: Suggestion
  onEditNotifications?: (suggestionId: string, notifications: any[]) => void
}

export const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  suggestion,
  onEditNotifications
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editedNotifications, setEditedNotifications] = useState(suggestion.notifications)

  // Channel icons
  const getChannelIcon = (channel: string): string => {
    switch (channel.toLowerCase()) {
      case 'sms': return '📱'
      case 'email': return '📧'
      case 'whatsapp': return '💬'
      case 'phone': return '📞'
      case 'in_person': return '🤝'
      default: return '📬'
    }
  }

  // Channel colors
  const getChannelColor = (channel: string): string => {
    switch (channel.toLowerCase()) {
      case 'sms': return '#4caf50'
      case 'email': return '#2196f3'
      case 'whatsapp': return '#25d366'
      case 'phone': return '#ff9800'
      case 'in_person': return '#9c27b0'
      default: return '#757575'
    }
  }

  // Start editing a notification
  const handleEditStart = (index: number) => {
    setEditingIndex(index)
  }

  // Save edited notification
  const handleEditSave = (index: number, newMessage: string) => {
    const updated = [...editedNotifications]
    updated[index] = { ...updated[index], message: newMessage }
    setEditedNotifications(updated)
    setEditingIndex(null)
    
    if (onEditNotifications) {
      onEditNotifications(suggestion.id, updated)
    }
  }

  // Cancel editing
  const handleEditCancel = () => {
    setEditingIndex(null)
  }

  // Format time
  const formatTime = (date: Date): string => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Get message preview (first 100 chars)
  const getMessagePreview = (message: string): string => {
    return message.length > 100 ? `${message.substring(0, 100)}...` : message
  }

  return (
    <div className="notification-preview">
      {/* Header */}
      <div className="preview-header">
        <h3 className="preview-title">
          Notification Plan ({suggestion.notifications.length} messages)
        </h3>
        <p className="preview-description">
          Preview and customize the messages that will be sent to vendors and stakeholders.
        </p>
      </div>

      {/* Notification Timeline */}
      {suggestion.notifications.length > 0 ? (
        <div className="notification-timeline">
          {editedNotifications.map((notification, index) => (
            <div key={index} className="notification-item">
              {/* Timeline Connector */}
              <div className="timeline-connector">
                <div 
                  className="timeline-dot"
                  style={{ backgroundColor: getChannelColor(notification.channel) }}
                />
                {index < editedNotifications.length - 1 && (
                  <div className="timeline-line" />
                )}
              </div>

              {/* Notification Content */}
              <div className="notification-content">
                {/* Header */}
                <div className="notification-header">
                  <div className="recipient-info">
                    <span className="recipient-name">{notification.recipient}</span>
                    <span className="recipient-role">{notification.recipientRole}</span>
                  </div>
                  
                  <div className="channel-info">
                    <span 
                      className="channel-badge"
                      style={{ backgroundColor: getChannelColor(notification.channel) }}
                    >
                      <span className="channel-icon">{getChannelIcon(notification.channel)}</span>
                      {notification.channel.toUpperCase()}
                    </span>
                    <span className="timing-info">
                      {notification.timing === 'immediate' ? 'Now' : `In ${notification.delayMinutes}min`}
                    </span>
                  </div>
                </div>

                {/* Message Content */}
                <div className="message-content">
                  {editingIndex === index ? (
                    <div className="message-editor">
                      <textarea
                        className="message-textarea"
                        defaultValue={notification.message}
                        placeholder="Enter your message..."
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleEditSave(index, e.currentTarget.value)
                          } else if (e.key === 'Escape') {
                            handleEditCancel()
                          }
                        }}
                      />
                      <div className="editor-actions">
                        <button 
                          className="btn-cancel-edit"
                          onClick={handleEditCancel}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn-save-edit"
                          onClick={(e) => {
                            const textarea = e.currentTarget.parentElement?.previousElementSibling as HTMLTextAreaElement
                            handleEditSave(index, textarea.value)
                          }}
                        >
                          Save
                        </button>
                      </div>
                      <div className="editor-hint">
                        Press Ctrl+Enter to save, Esc to cancel
                      </div>
                    </div>
                  ) : (
                    <div className="message-display">
                      <div className="message-text">
                        {getMessagePreview(notification.message)}
                      </div>
                      <button 
                        className="btn-edit-message"
                        onClick={() => handleEditStart(index)}
                        title="Edit message"
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Message Details */}
                <div className="message-details">
                  <div className="detail-item">
                    <span className="detail-label">Priority:</span>
                    <span className={`priority-badge priority-${notification.priority}`}>
                      {notification.priority}
                    </span>
                  </div>
                  
                  {notification.requiresResponse && (
                    <div className="detail-item">
                      <span className="response-required">Response Required</span>
                    </div>
                  )}
                  
                  {notification.estimatedDeliveryTime && (
                    <div className="detail-item">
                      <span className="detail-label">Est. Delivery:</span>
                      <span className="delivery-time">
                        {formatTime(notification.estimatedDeliveryTime)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-notifications">
          <div className="no-notifications-icon">📭</div>
          <h4>No Notifications Needed</h4>
          <p>This suggestion doesn't require any vendor or stakeholder notifications.</p>
        </div>
      )}

      {/* Notification Summary */}
      {suggestion.notifications.length > 0 && (
        <div className="notification-summary">
          <h4 className="summary-title">Delivery Summary</h4>
          <div className="summary-stats">
            <div className="stat-group">
              <span className="stat-label">Total Messages:</span>
              <span className="stat-value">{suggestion.notifications.length}</span>
            </div>
            
            <div className="stat-group">
              <span className="stat-label">Immediate:</span>
              <span className="stat-value">
                {suggestion.notifications.filter(n => n.timing === 'immediate').length}
              </span>
            </div>
            
            <div className="stat-group">
              <span className="stat-label">Delayed:</span>
              <span className="stat-value">
                {suggestion.notifications.filter(n => n.timing === 'delayed').length}
              </span>
            </div>
            
            <div className="stat-group">
              <span className="stat-label">High Priority:</span>
              <span className="stat-value">
                {suggestion.notifications.filter(n => n.priority === 'high').length}
              </span>
            </div>
          </div>
          
          {/* Channel Breakdown */}
          <div className="channel-breakdown">
            <span className="breakdown-label">By Channel:</span>
            <div className="channel-counts">
              {Array.from(new Set(suggestion.notifications.map(n => n.channel))).map(channel => (
                <span 
                  key={channel}
                  className="channel-count"
                  style={{ color: getChannelColor(channel) }}
                >
                  {getChannelIcon(channel)} {channel}: {suggestion.notifications.filter(n => n.channel === channel).length}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Best Practices Note */}
      <div className="best-practices">
        <h4>💡 Communication Best Practices</h4>
        <ul>
          <li>Critical changes are sent via multiple channels for reliability</li>
          <li>Vendors receive updates in order of their timeline dependencies</li>
          <li>Response timeouts ensure timely acknowledgment of important changes</li>
          <li>All messages follow venue-specific communication templates</li>
        </ul>
      </div>
    </div>
  )
}

export default NotificationPreview